
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useParams } from "react-router-dom";
import React from 'react';
import Index from "./pages/Index";
import Robos from "./pages/Robos";
import Dashboard from "./pages/Dashboard";
import Duocore from "./pages/Duocore";
import ObrigadoEspera from "./pages/ObrigadoEspera";
import NotFound from "./pages/NotFound";

// Simple 404 component - static files handled by Netlify with 200! priority
const SmartNotFound = () => <NotFound />;

// Componente universal para servir arquivos estáticos da pasta public
const StaticFileComponent = ({ filename }: { filename: string }) => {
  const [content, setContent] = React.useState<string>('');
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string>('');

  // Mapear extensões para content-types
  const getContentType = (filename: string) => {
    const extension = filename.split('.').pop()?.toLowerCase();
    const contentTypes: Record<string, string> = {
      'xml': 'application/xml',
      'txt': 'text/plain',
      'json': 'application/json',
      'png': 'image/png',
      'jpg': 'image/jpeg',
      'jpeg': 'image/jpeg',
      'gif': 'image/gif',
      'ico': 'image/x-icon',
      'svg': 'image/svg+xml',
      'css': 'text/css',
      'js': 'application/javascript',
      'html': 'text/html',
      'pdf': 'application/pdf',
      'zip': 'application/zip'
    };
    return contentTypes[extension || ''] || 'text/plain';
  };

  // Verificar se é arquivo de imagem
  const isImageFile = (filename: string) => {
    const extension = filename.split('.').pop()?.toLowerCase();
    return ['png', 'jpg', 'jpeg', 'gif', 'ico', 'svg', 'webp'].includes(extension || '');
  };

  // Verificar se é arquivo válido (tem extensão conhecida)
  const isValidFile = (filename: string) => {
    const extension = filename.split('.').pop()?.toLowerCase();
    const validExtensions = [
      'png', 'jpg', 'jpeg', 'gif', 'ico', 'svg', 'webp', // imagens
      'xml', 'txt', 'json', 'pdf', 'css', 'js', 'html', 'zip' // outros
    ];
    return validExtensions.includes(extension || '');
  };

  // Se não é um arquivo válido, mostrar 404
  if (!isValidFile(filename)) {
    return <SmartNotFound />;
  }

  React.useEffect(() => {

    const fetchFile = async () => {
      try {
        const response = await fetch(`/${filename}?v=${Date.now()}`);
        if (!response.ok) {
          throw new Error(`Arquivo não encontrado: ${filename}`);
        }
        
        if (isImageFile(filename)) {
          // Para imagens, mostrar no navegador
          setContent(`IMAGE:${filename}`);
          setLoading(false);
          return;
        }
        
        const text = await response.text();
        setContent(text);
        setLoading(false);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Erro ao carregar arquivo');
        setLoading(false);
      }
    };

    fetchFile();
  }, [filename]);

  // Configurar content-type
  React.useEffect(() => {
    if (!loading && !error) {
      const metaTag = document.createElement('meta');
      metaTag.httpEquiv = 'Content-Type';
      metaTag.content = `${getContentType(filename)}; charset=utf-8`;
      document.head.appendChild(metaTag);

      return () => {
        const existingMeta = document.head.querySelector('meta[http-equiv="Content-Type"]');
        if (existingMeta) {
          document.head.removeChild(existingMeta);
        }
      };
    }
  }, [filename, loading, error]);

  if (loading) {
    return <div>Carregando...</div>;
  }

  if (error) {
    return <SmartNotFound />;
  }

  // Para imagens, mostrar elemento img
  if (content.startsWith('IMAGE:')) {
    const imagePath = content.replace('IMAGE:', '');
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', padding: '20px' }}>
        <img 
          src={`/${imagePath}`} 
          alt={imagePath}
          style={{ maxWidth: '100%', maxHeight: '100vh', objectFit: 'contain' }}
        />
      </div>
    );
  }

  // Para arquivos de texto/XML, mostrar como preformatado
  return (
    <pre style={{ margin: 0, fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}>
      {content}
    </pre>
  );
};

// Wrapper para usar useParams
const StaticFileRouter = () => {
  const { filename } = useParams<{ filename: string }>();
  return filename ? <StaticFileComponent filename={filename} /> : <SmartNotFound />;
};

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Index />} />
            <Route path="/robos" element={<Robos />} />
            <Route path="/duocore" element={<Duocore />} />
            <Route path="/obrigado-espera-copy" element={<ObrigadoEspera />} />
            <Route path="/dashboard" element={<Dashboard />} />
          {/* Rota dinâmica para arquivos estáticos */}
          <Route 
            path="/:filename" 
            element={<StaticFileRouter />} 
          />
          <Route path="*" element={<SmartNotFound />} />
        </Routes>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
